import { getDateWithoutTime } from '../../../../../Helpers/DateFormatter';
import { MDBox } from '../../MDBox';
import { ComboChart } from '../Base/ComboChart/ComboChart';
import { IComboChartData } from '../Base/ComboChart/IComboChartData';
import { ResultWithValue } from '../../../Constants/Result/ResultWithValue';
import { MDTypography } from '../../MDTypography';
import { DashboardService } from '../../../../Api/EmploymentEquity/Dashboard/DashboardService';
import { IRaceGenderGraphHeadings } from '../../../Constants/EEPlanRaceGenderPairsGraphHeadings';
import { EmploymentEquityDashboardDto } from '../../../../Model/Dto/EmploymentEquity/Dashboard';
import { mapEmployeeHeadcountToComboChartData } from '../../../../Model/Mappers/EmploymentEquityMapper';
import { Gender, RaceTypes } from '../../../../Model/Enum/BEE123Enums';
import { parseMonthYear } from '../../../../Model/helpers';

interface IProps {
  scorecardId: string;
  scorecardTypeId?: string;
  chartOptions?: any;
  dashboardLevelOnClick?: (chartLabel: string) => void;
  raceGenderPair: IRaceGenderGraphHeadings;
}


export const EmployementEquityPlanTrackingComboChart = (props: IProps) => {
  const emptyResult: ResultWithValue<IComboChartData> = {
    isSuccess: false,
    value: {
      datasets: [],
      labels: [],
    },
    errorMessage: "Data not ready",
  };

  const raceStringToEnumKeyMap: { [key: string]: keyof typeof RaceTypes } = {
    'African': 'African',
    'Coloured': 'Coloured',
    'Indian': 'Indian',
    'White': 'White',
    'Foreign National': 'NonRSA',
  };

  const onChartSegmentClick = (label: string) => {
    const { chartOptions, raceGenderPair, dashboardLevelOnClick } = props;
    const finalLabel = `${raceGenderPair.race}-${raceGenderPair.gender}|${chartOptions?.selectedOccupationalLevel?.label}|${parseMonthYear(label)}`;
    if (dashboardLevelOnClick) {
      dashboardLevelOnClick(finalLabel);
    }
  };

  const getChartData = () => {
    const { chartOptions } = props;
    const occupationalLevelId = chartOptions?.selectedOccupationalLevel.id;
    const raceString = props.raceGenderPair.race;
    const genderString = props.raceGenderPair.gender;

    const selectedYearId = chartOptions?.selectedYearId;
    const selectedYear = chartOptions?.selectedYear;

    if (!occupationalLevelId || !raceString || !genderString || !selectedYearId || !selectedYear) {
      console.warn("Chart data dependencies not ready. Skipping fetch.");
      return Promise.resolve(emptyResult);
    }

    const mappedRaceId = raceStringToEnumKeyMap[raceString];
    const raceId = RaceTypes[mappedRaceId];
    const genderId = Gender[genderString as keyof typeof Gender];

    const data: EmploymentEquityDashboardDto.EEPlanHeadcountRequest = {
      occupationalLevelId: occupationalLevelId,
      raceId: raceId,
      genderId: genderId,
      scorecardId: props.scorecardId,
      selectedYearId: selectedYearId,
      selectedYear: selectedYear
    };

    if(props.chartOptions.isEEPlanTrackingYear5){
      return DashboardService.GetEEPlan5YearTrackingHeadcount(data)
      .then(mapEmployeeHeadcountToComboChartData)
      .catch((err) => {
          console.error("GetEEPlanAnnualTrackingHeadcount fetch error", err);
          return emptyResult;
        }
      );
    }
    else {
      return DashboardService.GetEEPlanAnnualTrackingHeadcount(data)
      .then(mapEmployeeHeadcountToComboChartData)
      .catch((err) => {
          console.error("GetEEPlanAnnualTrackingHeadcount fetch error", err);
          return emptyResult;
        }
      );
    }
  }

  const filterDate = props?.chartOptions?.filterDate ?? getDateWithoutTime(new Date());
  const newHash = filterDate?.toISOString();
  return (
    <>
          <MDTypography ml={1}  variant="subtitle1" style={{ fontSize: '18px' }}>
            {props?.chartOptions?.selectedOccupationalLevel?.label}
          </MDTypography>

      <MDBox sx={{ margin: 1 }}>
        <ComboChart
          onSegmentClick={onChartSegmentClick}
          fetchData={getChartData}
          firstChartSeriesStart={0}
          secondChartSeriesStart={1}
          thirdChartSeriesStart={2}
          firstChartType="line"
          secondChartType={props.chartOptions.isEEPlanTrackingYear5 ? "steppedArea" : "line"}
          thirdChartType="line"
          isStacked={false}
          isEEPlanTracking={true}
          chartOptions={{
            legend: { position: "bottom" },
            isPowerPointPrint: false,
          }}
          hash={newHash}
          clickableSeriesIndex={0}
        />
      </MDBox>
    </>
  );
}

